import { Injectable } from '@angular/core';

@Injectable({
    providedIn: 'root'
})
export class AES {
    private readonly IV_SIZE = 12; // GCM standard: 96-bit IV
    private readonly TAG_SIZE_BITS = 128;
    private readonly KEY = 'aclE4@IGEncrypt5';

    // ── Internal helpers ──────────────────────────────────────────────────────

    private async importKey(): Promise<CryptoKey> {
        // Encode the key string as UTF-8 bytes, padded/truncated to 16 bytes (AES-128)
        const rawKey = new TextEncoder().encode(this.KEY.padEnd(16, '0').slice(0, 16));
        return crypto.subtle.importKey('raw', rawKey, { name: 'AES-GCM' }, false, ['encrypt', 'decrypt']);
    }

    // ── Public API ────────────────────────────────────────────────────────────

    /**
     * Encrypts plaintext with AES-GCM.
     * Format: Base64( [IV (12 bytes)] + [ciphertext + tag (16 bytes)] )
     *
     * Note: Web Crypto AES-GCM appends the auth tag to the ciphertext automatically.
     */
    async encryptGcm(plaintext: string): Promise<string> {
        const iv = crypto.getRandomValues(new Uint8Array(this.IV_SIZE));
        const key = await this.importKey();
        const encoded = new TextEncoder().encode(plaintext);

        const ciphertextWithTag = await crypto.subtle.encrypt({ name: 'AES-GCM', iv, tagLength: this.TAG_SIZE_BITS }, key, encoded);

        // Combine [IV | ciphertext | tag] — Web Crypto already appends the tag
        const combined = new Uint8Array(iv.byteLength + ciphertextWithTag.byteLength);
        combined.set(iv, 0);
        combined.set(new Uint8Array(ciphertextWithTag), iv.byteLength);

        return btoa(String.fromCharCode(...combined));
    }

    /**
     * Decrypts a Base64 AES-GCM string produced by encryptGcm().
     * Throws if authentication fails (tampered data).
     */
    async decryptGcm(encryptedBase64: string): Promise<string> {
        const combined = Uint8Array.from(atob(encryptedBase64), (c) => c.charCodeAt(0));

        const iv = combined.slice(0, this.IV_SIZE);
        const ciphertextWithTag = combined.slice(this.IV_SIZE); // tag is last 16 bytes, Web Crypto handles extraction

        const key = await this.importKey();

        let decrypted: ArrayBuffer;
        try {
            decrypted = await crypto.subtle.decrypt({ name: 'AES-GCM', iv, tagLength: this.TAG_SIZE_BITS }, key, ciphertextWithTag);
        } catch {
            throw new Error('Decryption failed: authentication tag mismatch or corrupted data.');
        }

        return new TextDecoder().decode(decrypted);
    }
}
